import { Focusable } from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import React, { useCallback, useRef, useState } from "react";
import { ProfilePinProps } from "../Pin/Pin";
import styles from "./styles.module.scss";

import { apiService } from "@blacknut/javascript-sdk/dist";
import { ReactComponent as VisibilityOn } from "../../assets/ic_visibility.svg";
import { ReactComponent as VisibilityOff } from "../../assets/ic_visibility_off.svg";

const SingleInputPin = ({
  className,
  value,
  errored,
  onPinEntered,
  handlePinChanged,
  pinEditing,
  setPinEditing,
  size = "medium",
  secured = true,
}: Omit<ProfilePinProps, "useVirtualKeyboard">) => {
  const x = new Array(4).fill(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectionStart, setSelectionStart] = useState(-1);
  const [isPinHidden, setIsPinHidden] = useState<boolean>(true);
  const emptyValue = useRef(true);

  const onSelect = useCallback((e) => {
    setSelectionStart(inputRef.current?.selectionStart || 0);
  }, []);
  const togglePinHidden = React.useCallback(() => {
    setIsPinHidden((b) => !b);
  }, []);

  const onChange = useCallback(
    (s: string) => {
      if (!s.match(/\d*/)) return;
      if (!pinEditing && setPinEditing) setPinEditing(true);
      handlePinChanged?.(s);
      if (s.length === 4) {
        emptyValue.current = true;
        if (inputRef.current) inputRef.current.scrollLeft = 0;
        onPinEntered?.(s);
      }
    },
    [handlePinChanged, onPinEntered, pinEditing, setPinEditing]
  );

  const onInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const s = e.target.value.trim();
      onChange(s);
    },
    [onChange]
  );
  const clearSelectionStart = useCallback(() => {
    setSelectionStart(-1);
  }, []);

  const onScroll = useCallback(() => {
    if (inputRef.current) inputRef.current.scrollLeft = 0;
  }, []);

  const onBlur = useCallback(() => {
    inputRef.current?.setSelectionRange(0, 0);
  }, []);

  const onKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (inputRef.current?.selectionStart !== 3) {
          //Enter not on the last char
          e.preventDefault();
          const s = inputRef.current?.value || "";
          onChange(s.slice(0, inputRef.current?.selectionStart || 0));
        }
      }
    },
    [onChange]
  );
  const _secured = (pinEditing && isPinHidden) || (!pinEditing && secured);
  return (
    <div
      className={clsx(
        styles.container,
        errored && styles.errored,
        size === "small" && styles.small,
        size === "large" && styles.large,
        className
      )}
      role="code"
    >
      <input
        className={styles.input}
        type={_secured ? "password" : "text"}
        autoComplete="one-time-code"
        inputMode={
          // Fix on AmazonTV when secured and numeric is set, a strange virtual keyboard appears
          apiService.userAgent.os === "Fireos" && _secured
            ? "decimal"
            : "numeric"
        }
        maxLength={4}
        pattern="\d{4}"
        onSelect={onSelect}
        ref={inputRef}
        value={value}
        onInput={onInput}
        onScroll={onScroll}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
      />

      {x.map((e, index) => {
        const onClick = () => {
          if (!inputRef.current) return;
          if (emptyValue.current) {
            emptyValue.current = false;
            handlePinChanged?.("");
          }
          if (index < inputRef.current.value.length) {
            inputRef.current.setSelectionRange(index, index + 1);
          }
          inputRef?.current?.focus();
        };
        const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (!inputRef.current) return;
          if (!e.key.match(/\d/)) return;
          if (index <= inputRef.current.value.length) {
            let s: string;
            if (emptyValue.current) {
              emptyValue.current = false;
              s = e.key;
            } else {
              s =
                inputRef.current.value.slice(0, index) +
                e.key +
                inputRef.current.value.slice(index + 1);
            }
            onChange(s);
            if (s.length !== 4) {
              (e.currentTarget.nextSibling as HTMLElement).focus();
            }
          }
        };
        return (
          <Focusable
            key={`${index}`}
            role="input-wrapper"
            className={clsx(
              styles.bkg,
              index === selectionStart && styles.active
            )}
            style={{ left: `calc(3ch * ${index})` }}
            onPressEnter={onClick}
            onClick={onClick}
            onKeyPress={onKeyPress}
            onFocus={clearSelectionStart}
          >
            <div className={styles.selection} />
          </Focusable>
        );
      })}

      {pinEditing && (
        <Focusable className={styles.eye} onPressEnter={togglePinHidden}>
          {isPinHidden ? (
            <VisibilityOff onClick={togglePinHidden} />
          ) : (
            <VisibilityOn onClick={togglePinHidden} />
          )}
        </Focusable>
      )}
    </div>
  );
};

export { SingleInputPin };
export default SingleInputPin;
